/* File defines the function that renders an episode cutscene. */
/* This is the most complicated part of the interpreter. */

import { PNCTypes, Dictionary, IntegerDictionary, LANGUAGE_NAMES } from "./TypesAndEnums";
import { Globals } from "./main";
//import { Vector2 as Vector2D } from "./vector2";
import * as Helpers from "./helpers"
import { AvgCharacter } from "./database";

export namespace AvgConfig {
	export interface Branch {
		
		disableSelected?:boolean;
		finalAct?:number;
		[luaNumberedIndex: number]: BranchCfg;
	}

	export interface BranchCfg {
		jumpAct:number;
		content:string|number;
	}
}

interface AvgPortraitState {
	/** A key that points to the portrait database. Looking this up will get you a PortraitData. */
	texture_id : string,
	position : number,
	dim : boolean,
	isMasked : boolean,
	face : number,
	/** Inserted at runtime... */
	data ?: AvgCharacter
}

interface InterpreterState {
	
	numStoryBoxRendered:number,
	loadedBGs:IntegerDictionary<PNCTypes.AvgBackground>,
	loadedPortraits:IntegerDictionary<PNCTypes.AvgPortrait>,
	currentPortraits:IntegerDictionary<AvgPortraitState>,
	isMale: boolean,
	speakerMap:Map<number,string>,
	textureMap:Map<string,AvgCharacter>,
	//commMap: new Map(),
}

/** 
 * Outer is stuff that exists only in this call (so they get reset in recursive children) but persist outside of the frame loop.
*/
interface InterpreterOuterState {
	lastSpeakerName:string;
	frameSaturation:number;
	currentBGs:IntegerDictionary<PNCTypes.AvgBackground>
}

//For CSS
var int2str = ['zero','one',  'two',   'three','four',  'five', 'six',  'seven',  'eight', 'nine'];
var int2strPos = ['N','first','second','third','fourth','fifth','sixth','seventh','eighth','ninth'];


/**
 * Renders PNC story scene given the AVG_CFG and AVG_LANG json. 
 * This function does not cause side effects but behavior is dependent on other loaded globals.
 * It mainly just does setup before it calls the inner function. 
 * @param {Object.<number, Object>} AVG_CFG 
 * @param {Object.<number, string|number>} AVG_LANG
 * @param {number} part Part number (for storyBox and frame IDs)
 * @returns {any} The rendered div for the given part
 */
export async function renderPNCStructToDiv(
		AVG_CFG:IntegerDictionary<object>, 
		AVG_LANG:IntegerDictionary<string>, 
		part:number=0
	) {
	let thisFragment = document.createDocumentFragment();
	let thisPartDiv = document.createElement('div');

	/* Package of config stuff for recursive running, TODO: name it something less vague */
	const cs:InterpreterState = {
		numStoryBoxRendered: 0,
		loadedBGs: {},
		loadedPortraits: {},
		currentPortraits: {},
		isMale: Globals.SETTINGS.playerGender === "m",

		//TODO: Get rid of this
		speakerMap: new Map(),
		textureMap: new Map(Object.entries(await Globals.DATABASE.getCharacterPortraits(['ALL']))),
		//commMap: new Map(),
	};

	//TODO: Get rid of this and use a global speaker map
	async function lookupMapConstruction(AVG_CFG:IntegerDictionary<object>):Promise<Map<number, string>> {
		let speakerSet:Set<number> = new Set();
		for(const frame of Object.values(AVG_CFG)) {
			if(frame.speakerHeroId) {
				speakerSet.add(frame.speakerHeroId);
			}
		}
		return Globals.DATABASE.getSpeakersByLanguage(speakerSet, Globals.CurrentLanguage)
	}
	cs.speakerMap = await lookupMapConstruction(AVG_CFG);
	console.log("Speakers in this file: ",cs.speakerMap);

	
	//await lookupMapConstruction(AVG_CFG, cs);

	await renderInnerPNCStructToDiv(thisFragment, AVG_CFG, AVG_LANG, cs, part);
	thisPartDiv.appendChild(thisFragment);
	return thisPartDiv;
}

/* Inner function that can have recursive calls for rendering the cutscene. */
async function renderInnerPNCStructToDiv(
	div:DocumentFragment, 
	AVG_CFG:IntegerDictionary<object>, 
	AVG_LANG:IntegerDictionary<string>, 
	cs:InterpreterState, 
	part:number, iterator = 1, end = 1024) {


	//What the fuck is currentBGs for? It should be in CS?
	const outer:InterpreterOuterState = {
		lastSpeakerName: "",
		frameSaturation: 1.0,
		//TODO: PNC has draw order based on loaded order so this should be an array not a dict
		currentBGs: {}
	};
	
	const MAX_ITERATIONS = 1024;
	let frame:Dictionary<any> = AVG_CFG[iterator];

	for (let loopCounter = 1; frame && loopCounter <= MAX_ITERATIONS; ++loopCounter, (frame = AVG_CFG[iterator])) {
		if(iterator == end) return iterator;
		/* Stuff that is reset every time the frame loops. */
		const loop = {
			didBGChange: false,
			didPortraitsChange: false,
			didNameChange: false,
			speaker: "",
			text: "",
			lastTextID: -1,
			//episode: curEpKey,
			frameId: `${part}-${iterator}`
		};

		/* Convert micaval to css. Add 1 (typecast) if saturation <= 0 to convert negative to percentage. */
		const saturation = frame?.ppv?.cg?.saturation;
		if (saturation !== undefined) {
			outer.frameSaturation = (saturation / 100) + Number(saturation <= 0);
		}

		
		/* Load a list of images that are declared in the avgcfg. */
		if (frame['images']) {
			Object.values(frame['images']).forEach((imgData:PNCTypes.AvgImage) => {
				const imgId = imgData['imgId'];
				if (imgData['delete']) {
					delete cs.loadedPortraits[imgId];
					delete cs.currentPortraits[imgId];
					delete cs.loadedBGs[imgId]; //TODO: This should not be a dict....
				} else {
					/* Backgrounds */
					if (imgData.imgType == PNCTypes.AvgImageType.BACKGROUND || imgData.imgType == PNCTypes.AvgImageType.VIDEO || imgData['fullScreen']) {
						cs.loadedBGs[imgId] = <PNCTypes.AvgBackground>imgData;
						//Since JS keeps dict order based on when an object was added this works
						//outer.currentBGs[imgId] = imgData;
					} else {
						cs.loadedPortraits[imgId] = <PNCTypes.AvgPortrait>imgData;
					}
				}
			});
		}

		if (frame['heroFace']) {
			//Lord help me what the fuck are we doing here
			Object.values(frame['heroFace']).forEach((imgFaceData:any) => {
				if (imgFaceData.imgId in cs.loadedPortraits) {
					cs.loadedPortraits[imgFaceData.imgId].face = imgFaceData.faceId;
				}
			})
		}

		if (frame['imgTween']) {
			Object.values(frame['imgTween']).forEach( (tweenData:PNCTypes.AvgTweenData) => {
				const imgId = tweenData['imgId'];
				if (imgId in cs.loadedBGs) {
					const alpha = tweenData['alpha'];
					if (alpha != undefined)
						cs.loadedBGs[imgId].alpha = alpha;
					loop.didBGChange = true;
				} else if (cs.loadedPortraits[imgId]) { 
					
					const portrait = cs.loadedPortraits[imgId];

					if (tweenData['alpha'] > 0) {
						cs.currentPortraits[imgId] = {
							texture_id : (cs.isMale && portrait?.imgPathBoy) || portrait.imgPath,
							position : tweenData?.['posId'] || portrait.posId || 3,
							dim : tweenData['isDark'],
							isMasked : portrait.comm,
							face : portrait?.face || 0
						};
						// If the tween doesn't specify the posId, it gets reused from the last
						// set position... So we store the last set position somewhere
						cs.loadedPortraits[imgId].posId = cs.currentPortraits[imgId].position;
					} else {
						if (tweenData.posId)
							cs.loadedPortraits[imgId].posId = tweenData.posId;
						delete cs.currentPortraits[imgId];
					}
					loop.didPortraitsChange = true;
				} else {
					console.log(`Found an image tween with an ID that wasn't loaded: ${imgId}`);
				}
			});
		}

		if (frame['speakerHeroId'] || frame['speakerName']) {
			//SpeakerName takes priority over SpeakerHeroID if both are present.
			//loop.speaker = frame['speakerName'] ? AVG_LANG[frame['speakerName']] || "Error: Somehow speakerName not in AVG_LANG" : (cs.speakerMap.get(frame['speakerHeroId']) || "Error: Missing "+frame['speakerHeroId']+" in speakerMap");
			if (frame['speakerName']) {
				loop.speaker = AVG_LANG[frame['speakerName']] || "Error: Somehow speakerName "+frame['speakerName']+"not in AVG_LANG";
			} else {
				loop.speaker = cs.speakerMap.get(frame['speakerHeroId']) || "Error: Missing "+frame['speakerHeroId']+" in speakerMap";
			}
			
			if (loop.speaker == undefined || loop.speaker == "undefined") {
				console.error("Failed to obtain speaker name. speakerHeroId = ",frame['speakerHeroId'],", speakerName = ",frame['speakerName'])
				//console.error(AVG_LANG[frame['speakerName']])
				//console.error(cs.speakerMap);
			}
			if (loop.speaker != outer.lastSpeakerName) {
				loop.didNameChange = true;
			}
		}

		//The text that is being spoken in this frame. Gets used later in "doStoryText"
		if (frame['content']) {
			const avgLangContentKey = frame['content'];

			//If playing as a female check if there is a negative key in AVG_LANG
			if (cs.isMale==false && AVG_LANG.hasOwnProperty(avgLangContentKey*-1)) {
				loop.lastTextID = avgLangContentKey*-1;
				loop.text = AVG_LANG[loop.lastTextID];
			} else {
				loop.text = AVG_LANG[avgLangContentKey] || avgLangContentKey;
				loop.lastTextID = avgLangContentKey;
			}
		}
		/* Regular story box and text. */
		const frameDiv = document.createElement('div');
		frameDiv.setAttribute('id', loop.frameId);
		if (end != 1024) {
			frameDiv.setAttribute("branch","true");
		}
		
		//Audio handling
		if (frame['audio']) {
			const audioInformation = frame['audio'];
			if (audioInformation['bgm']) {
				if (audioInformation['bgm']['stop'] === true) {

				} else {
					//TODO TODO TODO TODO use cloneNode instead...
					const playBGMbutton = document.createElement('a');
					playBGMbutton.setAttribute("class","waves-effect waves-light btn");
					playBGMbutton.innerHTML='<i class="material-icons left">music_note</i>'+"Play audio for this scene"
					
	
					// For anyone that's trying to figure out what's going on here, PNC uses
					// WWise audio. Essentially tracks are stored in virtual CDs. So MICA
					// is calling it by the CD name and then the track name in the CD.
					// Since all the files called here only have one track, we can just
					// play whatever mp3 file matches the filename of the "CD".
					const trackName = audioInformation['bgm']['cue']
					playBGMbutton.setAttribute("onclick","playNewAudio('audio/"+trackName+"')")
					playBGMbutton.title = audioInformation['bgm']['sheet'] + "/"+trackName;
					if (Globals.SETTINGS.showDebugOpcodeButton)
					{
						playBGMbutton.innerHTML='<i class="material-icons left">music_note</i>'+trackName
					}
					frameDiv.appendChild(playBGMbutton);
				}
			}
			if (audioInformation['sfx'] && Globals.SETTINGS.showSFXButtons) {

				const trackName = audioInformation['sfx']['cue']

				const playSFXbutton = document.createElement('a');
				playSFXbutton.setAttribute("class","waves-effect waves-light btn-small");
				playSFXbutton.innerHTML='<i class="material-icons left">surround_sound</i>SFX: '+trackName
				playSFXbutton.setAttribute("onclick","playSFX('sfx/"+trackName+"')")
				frameDiv.appendChild(playSFXbutton);
			}
		}

		
		//As far as I can tell, frames with branches only have tweens, audio, and expression changes. Never text.
		if (frame['branch']) {
			//pass
		} else {
			doStoryBox(frameDiv, cs, outer, loop, part);

			if (loop.text) {
				//doStoryText(frame, frameFrag, loop);
				if (loop.didNameChange) {
					const speakerParagraphElem = document.createElement('p');
					if (loop.speaker === "bravo" || loop.speaker === "Me") {
						speakerParagraphElem.innerText = Globals.SETTINGS.playerName || Globals.DefaultPlayerName;
					} else {
						speakerParagraphElem.innerHTML = Helpers.parsePNCTextModifiers(loop.speaker);
					}
					speakerParagraphElem.classList.add("storyText");
					speakerParagraphElem.classList.add("speakerName");
					frameDiv.appendChild(speakerParagraphElem);
				}
				const p = document.createElement('p');
				p.classList.add('storyText');
				p.setAttribute("title", "Text ID: "+frame['content']+ " | Current Frame: "+iterator);
				p.innerHTML = Helpers.parsePNCTextModifiers(loop.text);
				frameDiv.appendChild(p);
			} else if (frame['vedioPath']) {
				frameDiv.appendChild(doVideo(frame['vedioPath']));
			}
		}

		
		div.appendChild(frameDiv);
			

		if (frame['branch']) {
			/* Branch can modify the iterator, but you can't pass it by reference so we return it (maybe) modified */
			iterator = doBranch(div, frameDiv, AVG_CFG, AVG_LANG, cs, frame, part, iterator, end); 
			if(!iterator) {
				break; /* The one edge case error that is supposed to break the loop. */
			}
			//We've already modified the iterator while checking the branch above!!! So now we have to render the new index.
			continue;
		} else if (frame['isEnd'])
			break;
		else if (frame['nextId']) {
			iterator = frame['nextId'];
		} else {
			iterator++;
		}

		outer.lastSpeakerName = loop.speaker;

		if (loopCounter == MAX_ITERATIONS)
		{
			console.error(`Table parser failed at 4096 instructions, something is wrong with this script! Last iterator was ${iterator}`);
		}
	}
	return iterator;
}

function doStoryBox(frameDiv:DocumentFragment|HTMLDivElement, cs:InterpreterState, outer:InterpreterOuterState, loop, part:number) {
	if (Globals.SETTINGS.displayImagesAndPortraits && (loop.didBGChange || loop.didPortraitsChange))
	{
		let boxFrag:DocumentFragment = document.createDocumentFragment();
		let storyBox = document.createElement('div');

		/* Prepare settings-based attributes. */
		if (Globals.SETTINGS.quickScroll)
		{
			// Why use storyBox instead of iterator? Well, we don't actually know if the next iterator is iterator + 1
			storyBox.style.cursor='pointer';
			storyBox.setAttribute('id',`storyBox${part}-${cs.numStoryBoxRendered}`);
			storyBox.setAttribute('onclick', `document.getElementById('storyBox${part}-${cs.numStoryBoxRendered+1}')?.scrollIntoView({behavior:'auto',block:'center',inline:'center'})`);
			cs.numStoryBoxRendered++;
		}
		/*if (Globals.SETTINGS.keyboardNavigation)
		{
			storyBox.setAttribute("tabindex","0");
			storyBox.addEventListener("keydown", keyboardHandlerForStoryBox);
		}*/

		
		/* Due to Mica's inconsistent "flashback" cg implementation, we have to show all
		bgs even if they're empty. While this fixes missing flashback cgs, it leaves
		behind tons of blank story boxes from fade ins and outs and such. Only human
		intervention can fix it. We only need to skip rendering a background. We'll 
		never skip frames with portraits. */
		if(true) {
			storyBox.classList.add('storyImg');

			//WELCOME TO HELL
			let bgImage = document.createElement('div');
			bgImage.setAttribute('class','bg');

			const backgrounds = Object.values(cs.loadedBGs);

			for (let i = 0; i < backgrounds.length; i++) {
				const background = backgrounds[i];
				//'background' is an object here... I wish I had TypeScript right now
				if (background.alpha > 0) {

					//This is incorrect. Some backgrounds overlaid on top are transparent.
					//For now, we just have to hope MICA deletes backgrounds when they are completely covered...
					/*if (i<backgrounds.length-1 && backgrounds[i+1].alpha==1)
						continue;*/

					let bgLayer:HTMLImageElement|HTMLVideoElement;

					//For this ONE SINGLE FILE it shows it without any portraits and overlays text on top
					if (background.imgPath=="avg/sum23_blackhole_loop") {
						bgLayer = doVideo(background.imgPath, false);
						bgLayer.autoplay = true;
						bgLayer.loop = true;
					} else {

						bgLayer = document.createElement('img');
			
						//(cs.isMale && portrait?.imgPathBoy) || portrait.imgPath,
						bgLayer.src = `avgtexture/${cs.isMale && background?.imgPathBoy || background.imgPath || "cpt00/back"}.png`;
						if (background.alpha != 1) {
							bgLayer.style.opacity = background.alpha;
						}
					}


					if (Globals.SETTINGS.showDebugOpcodeButton)
					{
						bgLayer.setAttribute('title',`ID: ${background.imgId} | ${background.imgPath}`);
					}
					bgImage.appendChild(bgLayer);
				}
			}
			if (bgImage.childElementCount > 0)
				storyBox.appendChild(bgImage);
		}

		for (const [imgId, imgData] of Object.entries(cs.currentPortraits)) {
			//TypeScript stupid bullshit wtf
			let typedImgId:number = <number><unknown>imgId;
			let typedImgData:AvgPortraitState = <AvgPortraitState><unknown>imgData;

			const sprite = cs.textureMap.get(typedImgData.texture_id);
			if (imgData.isMasked) {
				storyBox.appendChild(generateCommPortrait(cs, sprite.texture, typedImgId, typedImgData));
			} else {
				storyBox.appendChild(generateRegularPortrait(cs, sprite.texture, typedImgId, typedImgData));
			}
		}
		
		/* Do saturation if necessary. */
		if(outer.frameSaturation != 1.0) {
			storyBox.style.filter = `saturate(${outer.frameSaturation})`;
		}

		storyBox.appendChild(boxFrag);
		frameDiv.appendChild(storyBox);
	}
}

/**
 * Generates a regular portrait
 * @param {InterpreterState} cs current interpreter state
 * @param {string} sprite The image path to display. This is different from pulling it from the textureMap since it might be a male sprite instead of female.
 * @param {number} imgRuntimeId The ID assigned to this image within the script. Only used for debugging.
 * @param {AvgPortraitState} imgData The current state of the portrait in the script with face expression, position
 * @returns {HTMLDivElement}
 */
function generateRegularPortrait(
	cs:InterpreterState, 
	sprite:string, 
	imgRuntimeId:number, 
	imgData:AvgPortraitState
):HTMLDivElement {
	//TODO: Remove cs
	const char:AvgCharacter = cs.textureMap.get(imgData.texture_id);

	const portraitDiv = document.createElement("div");
	portraitDiv.classList.add("storydoll");

	let portraitImg = document.createElement('img');
	portraitImg.classList.add("portrait");
	portraitImg.src = `avgcharacter/${sprite}.png`;

	portraitDiv.appendChild(portraitImg);

	if (imgData.face) {
		const portraitFaceImg = document.createElement("img");
		portraitFaceImg.src = `avgcharacter/face/${imgData.texture_id}_face_${imgData.face}.png`;
		//console.assert(char.sizeDelta != null);
		portraitFaceImg.setAttribute("style", 
			getFaceTexturePositionString(
				char.avgFacePos.x, 
				char.avgFacePos.y, 
				char.avgFaceSize.x, 
				1024.0/(char.sizeDelta || 768.0)
			)
		)
		portraitDiv.appendChild(portraitFaceImg)
	}

	//Lol lmao
	if (Globals.SETTINGS.oversizeTuringBug.has(imgData.texture_id)) {
		portraitDiv.style.height = "175%"
	}



	if (Globals.SETTINGS.showDebugOpcodeButton === true)
		portraitDiv.setAttribute('title',`ID: ${imgRuntimeId} | TEXTURE ID: ${imgData.texture_id} | POSITION: ${imgData.position} | EXPRESSION: ${imgData.face}`);
	
	if (imgData.dim)
		portraitDiv.classList.add('dim');
	portraitDiv.classList.add(int2strPos[imgData.position]);

	let transform = "";
	// This is obtained by right clicking and inspecting the element... The math is
	// complicated so don't worry about it.
	// max width = 790 so width is 395, image is sized by height and set to 120% so it's 395*1.20
	const IMAGE_SIZE = 474;

	//Convert a pixel value to a percentage here, basically we assume the image is always 474px
	//Translate functions move it 'n' px based on its own size, not the parent, so we divide by image size
	// if (char.hasOwnProperty('posXOverride')) {
	// 	transform += `TranslateX(${char.posXOverride / IMAGE_SIZE * 100}%) `;
	// }

	// if (char.hasOwnProperty('posYOverride')) {
	// 	transform += `TranslateY(${char.posYOverride / IMAGE_SIZE * 100}%) `;
	// }

	/*
	manual scale removed, but might come back later
	if (char.scale != "NONE") {
		transform += `scale(${char.scale}) `;
	}
	*/

	if(transform !== "") {
		//portraitDiv.setAttribute("style", `transform:${transform}`);
	}

	return portraitDiv;
}

/**
 * Generates a communication box portrait
 * @param {InterpreterState} cs current interpreter state
 * @param {string} sprite The image path to display. This is different from pulling it from the textureMap since it might be a male sprite instead of female.
 * @param {number} imgRuntimeId The ID assigned to this image within the script. Only used for debugging.
 * @param {AvgPortraitState} imgData The current state of the portrait in the script with face expression, position
 * @returns {HTMLDivElement}
 */
function generateCommPortrait(
	cs:InterpreterState, 
	sprite:string, 
	imgRuntimeId:number, 
	imgData:AvgPortraitState
):HTMLDivElement {
	let commDiv = document.createElement('div');
	if (Globals.SETTINGS.showDebugOpcodeButton)
		commDiv.setAttribute('title',`ID: ${imgRuntimeId} | TEXTURE ID: ${imgData.texture_id} | POSITION: ${imgData.position} | EXPRESSION: ${imgData.face}`);
	commDiv.style='position:absolute;width:100%;top:10%';
	commDiv.classList.add('maskcontainer');
	commDiv.classList.add(int2strPos[imgData.position]);


	let imgElement = document.createElement('img');
	imgElement.style.position = 'absolute';
	imgElement.style.width = '30%';
	
	if (imgData.dim)
		commDiv.classList.add('dim')
	let h = "<img src='asset/maskBox.png' style='position:absolute;width:30%'>"
	//width should be width of maskBox * 0.869209809 because width of this image is 86% of maskBox in terms of pixels
	h+="<div style='overflow:hidden;position:absolute;width:26.076%;left:3%;padding-top:1%;'>"
		//Don't forget display:block or the div containing this will be slightly too big. Because CSS is amazing.
		h+="<img src='asset/maskBox2.png' style='position:relative;z-index:1;width:100%;display:block;'>"
		//How the fuck is it 270%? Or left -85%? How does this shit make ANY sense? WHY DO I NEED PADDING-TOP 4%?????
		h+="<img src='avgcharacter/"+sprite+".png' class='"+(imgData.dim?"dim":"")+"' style='position:absolute; min-width:270%; top:0; left:-85%; width:100%; padding-top:4%;'>"
	h+="</div>"
	commDiv.innerHTML=h
	return commDiv;
}

//TODO: Remove or inline this shit we don't really need a whole class and js file for this
function Vector2(x,y)
{
	//@ts-ignore
	return new Vector2D(x,y)
}

function getNormalizedFacePosition(x:number, y:number, face_size:number, viewport_scale=100/75):{x: number, y: number}
{
	let position = Vector2(x, y*-1) //Unity Y-Axis is flipped!
	let normalized = Vector2(512,512).add(position.multiply(viewport_scale))
	return normalized.subtract(Vector2(face_size, face_size).multiply(viewport_scale).divide(2))
}

function getFaceTexturePositionString(x:number, y:number, face_size=128.0, viewport_scale=100/75)
{
	//PNC uses a viewport of 1024px so we calculate based on that
	//Since we use CSS the actual destination texture size is irrelevant, it will always scale up and down
	const VIEWPORT_SIZE = 1024
	const FACE_TEXTURE_VIEWPORT_SIZE = 128

	let normalized = getNormalizedFacePosition(x, y, face_size, viewport_scale)
	//console.log(normalized.multiply(2).toString())
	let texture_scale_percent = face_size/FACE_TEXTURE_VIEWPORT_SIZE*viewport_scale

	let css_width = FACE_TEXTURE_VIEWPORT_SIZE/VIEWPORT_SIZE*texture_scale_percent*100.0;
	let css_left = normalized.x/VIEWPORT_SIZE*100.0;
	let css_top = normalized.y/VIEWPORT_SIZE*100.0;
	console.assert(css_width<100,"Failed to calculate proper face size!! Got "+css_width);

	return `
		width: ${css_width.toFixed(4)}%; 
		left: ${css_left.toFixed(4)}%; 
		top: ${css_top.toFixed(4)}%
	`
}

function doVideo(videoPath:string, controls=true):HTMLVideoElement {
	const video = document.createElement('video');
    video.style.width = '100%';
    video.controls = controls;

	video.addEventListener("play", function() {
		(document.getElementById('audioPlayer') as HTMLAudioElement).pause();
	});

    const source = document.createElement('source');
    source.src = `video/${videoPath}.webm`;
    source.type = 'video/webm';

    const fallbackText = document.createElement('p');
    fallbackText.textContent = 'Your browser does not support WEBM!! (PNC natively uses webm!!)';

    video.appendChild(source);
    video.appendChild(fallbackText);
    return video;
}

/* This function does branch stuff and returns the iterator if it was updated. Returns null on error. */
/* What parameters should we require for this function? All of them? Yeah ok. */
function doBranch(
	div:DocumentFragment, 
	frameDiv:HTMLDivElement,
	AVG_CFG:IntegerDictionary<any>, 
	AVG_LANG:IntegerDictionary<string>, 
	cs:InterpreterState,
	frame:Dictionary<any>, 
	part:number, 
	iterator:number, 
	end:number
):number {
	const branch:AvgConfig.Branch = frame['branch'];

	/* get num choices */
	const numChoices = (() => {
		let bIdx = 1;
		while (branch[bIdx]) bIdx++;
		return bIdx - 1;
	})();

	const choicesHaveDifferentDestinations = (function(arr: { jumpAct: number }[]){
		if (numChoices < 2) return false;

		const first = arr[0].jumpAct;
		return arr.some(obj => obj.jumpAct !== first);
	})(Object.values(branch));

	for(let branchIdx = 1; branchIdx <= numChoices; branchIdx++) {
		const c = branch[branchIdx];
		let choiceButton = document.createElement('a');
		choiceButton.setAttribute("class","waves-effect waves-light btn choiceButton");
		choiceButton.setAttribute("style","margin-right: 5px; margin-bottom: 5px; text-transform:unset;");
		choiceButton.innerHTML = Helpers.parsePNCTextModifiers(AVG_LANG[c.content] || c.content);
		if (Globals.SETTINGS.showDebugOpcodeButton) {
			choiceButton.innerHTML += " | jmp="+c.jumpAct;
		}
		choiceButton.addEventListener('click', () => {
			//If first element isn't found then this is a default choice without a header
			let firstFoundElement = document.getElementById(`choice-header-${part}-${c.jumpAct}`) || document.getElementById(`${part}-${c.jumpAct}`);
			firstFoundElement?.scrollIntoView({behavior: 'smooth'});
		});
		frameDiv.appendChild(choiceButton);
	}

	if (choicesHaveDifferentDestinations) {

		let runAheadIterations:number[][] = [];
		for(let branchIdx = 1; branchIdx <= numChoices; branchIdx++) {
			const c = branch[branchIdx];
			runAheadIterations.push(runAhead(AVG_CFG, c.jumpAct));
		}
		console.log("Exec runAhead, returned branches are ",runAheadIterations);
		//Next we have to make structs with begin:end so we know what to render.
		//Looping choices start at the c.jumpAct and end at the iterator.
		//Other choices start at c.jumpAct and end at where they converge
		let renderSections = runAheadIterations.map(arr => ({iterations: arr, begin: arr[0], end: 999, looping: false, branchIdx:0}));
		for(let i = 0, renderSection; renderSection = renderSections[i]; i++) {
			renderSections[i].branchIdx = i+1; //Remember, lua arrays start at 1
			/**
			 * Usually indicates a choice that loops forever.
			 * For example:
			 * 101 branches out to 102 and 106
			 * 102 -> 103 -> 104 -> 105 -> 101
			 */
			if (renderSections[i].iterations.includes(iterator)) {
				renderSections[i].end = iterator;
				renderSections[i].looping = true; //Just for filtering later
			}
		}
		//Now that we've filtered out the looping ones let's find the converging path
		//Holy garbage collection batman!
		let nonLoopingChoices = renderSections.filter(o => o.looping == false).map(o => o.iterations);

		//If branch contains finalAct nonLoopingChoices will be empty because both choices loop
		let convergingPathIdx = -1;
		if (nonLoopingChoices.length == 0) {
			convergingPathIdx = branch['finalAct'];
		} else if (nonLoopingChoices.length == 1) {
			//At this point we can remove it from the renderSections can't we?
			convergingPathIdx = nonLoopingChoices[0][0];
		} else {
			convergingPathIdx = findFirstCommonNumber(...nonLoopingChoices)
		}
		for(let i = 0, renderSection; renderSection = renderSections[i]; i++) {
			if (renderSection.looping == false) {
				renderSections[i].end = convergingPathIdx;
			}
		}

		if (convergingPathIdx == iterator && !branch['finalAct']) {
			const n = document.createElement("h4");
			n.innerText = `Infinite recursion (iterator didn't move). Giving up. iterator was ${iterator}. 
				Supposed to render up to ${end}`;
			n.setAttribute("style","color:red");
			div.appendChild(n);
			console.error(n.innerText);
			return null;
		}

		for(let i = 0; i < numChoices; i++) {
			const renderSection = renderSections[i]
			const c = branch[renderSection.branchIdx];
			if (c.jumpAct != convergingPathIdx) { //Don't render if this is the converging branch
				let h4 = document.createElement("h4");
				h4.setAttribute("id",`choice-header-${part}-${c.jumpAct}`)
				h4.innerText = `If choice ${renderSection.branchIdx} was picked...`;
				div.appendChild(h4);
				if (end == 1024) {
					renderInnerPNCStructToDiv(div, AVG_CFG, AVG_LANG, cs, part, renderSection.begin, renderSection.end);
					if (renderSection.looping && !branch['finalAct']) {
						let choiceButton = document.createElement('a');
						choiceButton.setAttribute("class","waves-effect waves-light btn choiceButton");
						choiceButton.setAttribute("style","margin-right: 5px; margin-bottom: 5px; text-transform:unset;");
						choiceButton.innerHTML = "Return to choices (This choice loops)";

						choiceButton.addEventListener('click', () => {
							document.getElementById(part+"-"+renderSection.end)?.scrollIntoView({behavior: 'smooth'});
						});
						div.appendChild(choiceButton);
					}
				} else {
					const n = document.createElement("h4");
					n.innerText = `Infinite recursion (loop inside loop). Giving up. iterator was ${iterator}. 
						Supposed to render ${renderSection.begin}-${renderSection.end}`;
					n.setAttribute("style", "color:red");
					div.appendChild(n);
					console.error(n.innerText);
				}
				div.appendChild(document.createElement("hr"));
			}
		}

		iterator = convergingPathIdx;
		
		
		
	}
	if (branch['finalAct']) {
		iterator = branch['finalAct'];
	}
	else if (!choicesHaveDifferentDestinations) {
//		debugger;
		iterator = branch['1']['jumpAct'];
	}

	return iterator;

	// /* New runAhead style walker. Tries to detect the end, or a loop. Works using alien technology from the future. */
	// /* Og runAhead still works in cases where the walker doesn't 100% work. Together they cover all scenarios. */
	// const walkPath = (start, target, max = 1024) => {
	// 	let iter = start;
	// 	const seen = new Set();
	// 	const path = [];
	// 	for (let i = 0; i < max; i++) {
	// 		path.push(iter);
	// 		if (iter === target) return { path, hitsTarget: true }; /* Returned to current. */
	// 		if (seen.has(iter)) return { path, loop: true }; /* Loop detected. */
	// 		seen.add(iter);

	// 		const f = AVG_CFG[iter];
	// 		if (!f) return { path, broken: true }; /* Irregularity detected. */
	// 		if (f.isEnd) return { path, end: true }; /* Found end. */
	// 		if (f.branch && iter !== start) return { path, stopsAtBranch: true }; /* Stops at branch: yes.*/

	// 		if (f.nextId != null) { iter = f.nextId; continue; }
	// 		if (AVG_CFG[iter + 1]) { ++iter; continue; }
	// 		return { path, broken: true }; /* Irregularity fallback. */
	// 	}
	// 	return { path, capped: true };
	// };

	// const doChoiceRendering = (cIdx, iter, jumpAct) => {
	// 	let h4 = document.createElement("h4");
	// 	h4.innerText = `If choice ${cIdx} was picked...`;
	// 	div.appendChild(h4);

	// 	if (end === 1024) {
	// 		renderInnerPNCStructToDiv(div, AVG_CFG, AVG_LANG, cs, part, jumpAct, iter);
	// 	} else {
	// 		const n = document.createElement("h4");
	// 		n.innerText = `Nested loop prevented rendering ${jumpAct} → ${iter}.`;
	// 		n.setAttribute("style", "color:red");
	// 		div.appendChild(n);
	// 	}
	// 	div.appendChild(document.createElement("hr"));
	// }

	// /* Render buttons and gather per-choice analysis. */
	// const choiceInfo = [];
	// for (let i = 1; i <= numChoices; ++i) {
	// 	const c:AvgConfig.BranchCfg = branch[i];
	// 	const btn = document.createElement('a');
	// 	btn.setAttribute("class", "waves-effect waves-light btn choiceButton");
	// 	btn.setAttribute("style", "margin-right: 5px; margin-bottom: 5px; text-transform:unset;");
	// 	btn.innerHTML = Helpers.parsePNCTextModifiers(AVG_LANG[c.content]);

	// 	/* Analyze path for loops back to current choices. */
	// 	const probe = walkPath(c.jumpAct, iterator, 1024);
	// 	const loopsBackToChoice = !!probe.hitsTarget;
	// 	const immediateLoop = (probe.path.length === 1 && loopsBackToChoice);

	// 	/* Detect disabled buttons (Mica's way of saying a choice loops, gets disabled ingame after being pressed once. */
	// 	const shouldDisable = !!branch.disableSelected && (loopsBackToChoice || immediateLoop);
		
	// 	div.appendChild(btn);
	// 	choiceInfo.push({ idx: i, choice: c, probe, disabled: shouldDisable, loopsBack: loopsBackToChoice });
	// }

	// /* Fast path: all choices jump to the same end. */
	// if (numChoices >= 1) {
	// 	let same = true;
	// 	const first = branch[1].jumpAct;
	// 	for (let i = 2; i <= numChoices; i++) {
	// 		if (branch[i].jumpAct !== first) { same = false; break; }
	// 	}
	// 	if (same) return first;
	// }

	// /* Jank path: compute reconvergence using a path Mica didn't mark as disabled (looping). */
	// const active = choiceInfo.filter(x => !x.disabled);
	// const runAheadPaths = active.map(a => runAhead(AVG_CFG, a.choice.jumpAct));
	// let reconvergeAt = null;

	// if (runAheadPaths.length == 1) {
	// 	reconvergeAt = runAheadPaths[0][runAheadPaths[0].length - 1] ?? active[0].choice.jumpAct;
	// } else if (runAheadPaths.length >= 2) {
	// 	const cand = findFirstCommonNumber(runAheadPaths[0], runAheadPaths[1]);
	// 	if (cand !== -1) reconvergeAt = cand;
	// }

	// /* If there's still no good place to reconverge, use finalAct. */
	// if (reconvergeAt == null || reconvergeAt == -1) {
	// 	if (branch.finalAct) {
	// 	reconvergeAt = branch.finalAct;
	// 	} else if (runAheadPaths.length > 0) {
	// 	/* Pick furthest forward last node. */
	// 	let best = null;
	// 	for (const p of runAheadPaths) {
	// 		const last = p[p.length - 1];
	// 		if (typeof last === 'number') best = (best == null) ? last : Math.max(best, last);
	// 	}
	// 	reconvergeAt = (best != null) ? best : iterator;
	// 	} else {
	// 	reconvergeAt = iterator; /* No active paths. */
	// 	}
	// }
	// /* For when every choice loops. */
	// if (active.length == 0) {
	// 	for (const a of choiceInfo) {
	// 		/* Display content until loop back to this choice. */
	// 		doChoiceRendering(a.idx, iterator, a.choice.jumpAct);
	// 	}
	// 	/* Prefer finalAct to advance, otherwise stop at choice. */
	// 	return branch.finalAct ? branch.finalAct : iterator;
	// }

	// /* For when some choices loop and others don't. */
	// for (const a of choiceInfo) {
	// 	let endAt = reconvergeAt;
	// 	/* This one loops so stop when we return to the choice. */
	// 	if (a.disabled && a.loopsBack) endAt = iterator;
	// 	/* Skip trivial case where jump target equals endAt (nothing to render) */
	// 	if (a.choice.jumpAct === endAt) continue;
	// 	doChoiceRendering(a.idx, endAt, a.choice.jumpAct);
	// }

	// /* Guard against “didn’t move” when there are active paths but reconvergeAt == iterator and no finalAct */
	// if (reconvergeAt === iterator && !branch.finalAct) {
	// 	const n = document.createElement("h4");
	// 	n.innerText = `Detected infinite loop. No safe reconvergence; keeping position at ${iterator}.`;
	// 	n.setAttribute("style", "color:red");
	// 	div.appendChild(n);
	// 	return iterator;
	// }
	// /* Advance iterator to reconvergeAt (or finalAct if chosen) */
	// return reconvergeAt;
}

// Helpers

/**
 * 
 * @param AVG_CFG The Avg
 * @param branchStart 
 * @returns 
 */
const runAhead = function(AVG_CFG:IntegerDictionary<object>, branchStart = 1): number[] {
	let iterator = branchStart;
	let iterations = [];

	for (let i = 0; i < 256; i++) {
		iterations.push(iterator);
		const frame = AVG_CFG[iterator];

		if (!frame) {
			break;
		}
		else if (frame['isEnd'] || frame['branch']) {
			break;
		}
		else if (frame['nextId']) {
			iterator = frame['nextId'];
		}
		else
		{
			iterator++;
		}
	}
	return iterations;
};

/**
 * Given multiple arrays, finds the first common number across all of them.
 * @param arrays - Two or more arrays of numbers
 * @returns The first number common to all arrays, or -1 if none exists
 */
const findFirstCommonNumber = function(...arrays: number[][]): number {
    if (arrays.length < 2) {
        throw new Error("At least two arrays are required");
    }

    // Start with a Set of the first array
    let common = new Set(arrays[0]);

    // Intersect with each subsequent array
    for (let i = 1; i < arrays.length; i++) {
        const nextSet = new Set(arrays[i]);
        common = new Set([...common].filter(n => nextSet.has(n)));

        if (common.size === 0) {
            return -1;
        }
    }

    // Now find the first element (in order) from the *last* array
    // that is still in the intersection
    const lastArray = arrays[arrays.length - 1];
    for (const num of lastArray) {
        if (common.has(num)) {
            return num;
        }
    }

    return -1;
};
